import React, { useCallback, useEffect, useState } from 'react';
import { Stethoscope, RefreshCw, Percent, Archive, ClipboardList, Banknote, Droplet, FileHeart } from 'lucide-react';
import { clinicalSettingsAPI, medicineAPI } from '../services/api';
import { useToast } from './UIProvider';
import Select from './Select';
import { ROUNDING_RULE_OPTIONS } from '../utils/money';

const DEFAULT_BILLING = {
  roundingRule: 'DOWN_1',
  cashRoundingRule: 'DOWN_10',
  receiptAutoPrint: true,
  receiptPaper: 'SLIP_80MM',
};

const PAPER_OPTIONS = [
  { value: 'SLIP_80MM', label: '80 mm slip' },
  { value: 'A4', label: 'A4 page' },
];

const readSugar = (data) => ({
  lancetId: data?.bedsideSugarLancetId || '',
  stripId: data?.bedsideSugarStripId || '',
});

const readBilling = (data) => ({
  roundingRule: data?.billingRoundingRule || DEFAULT_BILLING.roundingRule,
  cashRoundingRule: data?.billingCashRoundingRule || DEFAULT_BILLING.cashRoundingRule,
  receiptAutoPrint: data?.billingReceiptAutoPrint !== false,
  receiptPaper: data?.billingReceiptPaper === 'A4' ? 'A4' : 'SLIP_80MM',
});

const ClinicalSettingsManagement = ({ hasPermission }) => {
  const notify = useToast();

  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('SETTINGS_CLINICAL', action) : true),
    [hasPermission],
  );

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingIntake, setSavingIntake] = useState(false);
  const [markup, setMarkup] = useState('');
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [retention, setRetention] = useState('');
  const [savingRetention, setSavingRetention] = useState(false);
  const [billing, setBilling] = useState(DEFAULT_BILLING);
  const [savingBilling, setSavingBilling] = useState(false);
  // The two consumables a bedside blood sugar check uses. They cannot be worked out from the
  // catalogue: TEST_STRIP also holds the pregnancy strip, and the lancet sits in CONSUMABLE next
  // to surgical blades, so the clinic names them here once.
  const [sugar, setSugar] = useState({ lancetId: '', stripId: '' });
  const [savingSugar, setSavingSugar] = useState(false);
  const [supplies, setSupplies] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clinicalSettingsAPI.get();
      setSettings(data);
      setMarkup(data?.profitMarkupPercentage != null ? String(data.profitMarkupPercentage) : '0');
      setRetention(data?.deletedPatientRetentionDays != null ? String(data.deletedPatientRetentionDays) : '90');
      setBilling(readBilling(data));
      setSugar(readSugar(data));
    } catch (e) {
      notify('error', 'Failed to load clinical settings');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  const handleSaveRetention = async () => {
    const days = parseInt(retention, 10);
    if (isNaN(days) || days < 0) {
      notify('error', 'Enter a valid number of days (0 or more)');
      return;
    }
    setSavingRetention(true);
    try {
      const result = await clinicalSettingsAPI.setDeletedPatientRetention(days);
      setRetention(String(result.deletedPatientRetentionDays ?? days));
      notify('success', `Deleted-patient retention set to ${result.deletedPatientRetentionDays ?? days} days`);
    } catch (e) {
      notify('error', e.message || 'Failed to save retention');
    } finally {
      setSavingRetention(false);
    }
  };

  const handleSaveMarkup = async () => {
    const pct = parseFloat(markup);
    if (isNaN(pct) || pct < 0) {
      notify('error', 'Enter a valid markup percentage (0 or more)');
      return;
    }
    setSavingMarkup(true);
    try {
      const result = await clinicalSettingsAPI.setProfitMarkup(pct);
      setMarkup(String(result.profitMarkupPercentage ?? pct));
      notify('success', `Default markup set to ${result.profitMarkupPercentage ?? pct}%`);
    } catch (e) {
      notify('error', e.message || 'Failed to save markup');
    } finally {
      setSavingMarkup(false);
    }
  };

  // Every stocked supply, for the two pickers. Loaded once: the list is short and rarely changes.
  useEffect(() => {
    let cancelled = false;
    medicineAPI.getAll()
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : (rows?.content || []);
        // Supplies only. A lancet or a strip is never a medicine, and the medicine list is long.
        setSupplies(
          list
            .filter((m) => m.category === 'SUPPLY')
            .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        );
      })
      .catch(() => { if (!cancelled) setSupplies([]); });
    return () => { cancelled = true; };
  }, []);

  const handleSaveSugar = async () => {
    setSavingSugar(true);
    try {
      const result = await clinicalSettingsAPI.updateBilling({
        bedsideSugarLancetId: sugar.lancetId || '',
        bedsideSugarStripId: sugar.stripId || '',
      });
      setSugar(readSugar(result));
      notify('success', 'Blood sugar supplies saved');
      window.dispatchEvent(new Event('clinical-settings:changed'));
    } catch (e) {
      notify('error', e.message || 'Failed to save the blood sugar supplies');
    } finally {
      setSavingSugar(false);
    }
  };

  const handleSaveBilling = async () => {
    setSavingBilling(true);
    try {
      const result = await clinicalSettingsAPI.updateBilling({
        roundingRule: billing.roundingRule,
        cashRoundingRule: billing.cashRoundingRule,
        receiptAutoPrint: billing.receiptAutoPrint,
        receiptPaper: billing.receiptPaper,
      });
      setBilling(readBilling(result));
      notify('success', 'Billing settings saved');
      window.dispatchEvent(new Event('clinical-settings:changed'));
    } catch (e) {
      notify('error', e.message || 'Failed to save the billing settings');
    } finally {
      setSavingBilling(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleIntakeForm = async () => {
    if (!can('EDIT')) return;
    setSavingIntake(true);
    try {
      const result = await clinicalSettingsAPI.setIntakeFormEnabled(!settings.intakeFormEnabled);
      setSettings(prev => ({ ...prev, ...result }));
      notify(
        'success',
        result.intakeFormEnabled
          ? 'The first-visit medical history is being asked for again'
          : 'The first-visit medical history is suspended. Histories already taken are untouched.',
      );
      window.dispatchEvent(new Event('clinical-settings:changed'));
    } catch (e) {
      notify('error', e.message || 'Failed to update setting');
    } finally {
      setSavingIntake(false);
    }
  };

  const handleToggleSaveTemplate = async () => {
    if (!can('EDIT')) return;
    setSaving(true);
    try {
      const result = await clinicalSettingsAPI.setAllowSaveTemplate(!settings.allowSaveTemplateFromPrescription);
      setSettings(prev => ({ ...prev, ...result }));
      notify('success', result.allowSaveTemplateFromPrescription ? 'Save Template enabled in prescriptions' : 'Save Template hidden in prescriptions');
      window.dispatchEvent(new Event('clinical-settings:changed'));
    } catch (e) {
      notify('error', e.message || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-12 text-center">
          <RefreshCw className="h-8 w-8 text-emerald-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading clinical settings…</p>
        </div>
      </div>
    );
  }

  const saveTemplateOn = settings?.allowSaveTemplateFromPrescription === true;
  // Absent means on: a clinic with no answer recorded gets the form, the way the server reads it.
  const intakeOn = settings?.intakeFormEnabled !== false;
  const canEdit = can('EDIT');

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center space-x-4">
          <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
            <Stethoscope className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Clinical Settings</h1>
            <p className="text-gray-600">Operational toggles for the clinic workflow</p>
          </div>
        </div>

        {/* First-visit medical history. Suspendable, because the question catalogue gets reworked
            and a half-finished form should not stand between a doctor and a prescription. */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-start gap-4 flex-1">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${intakeOn ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                <FileHeart className={`h-6 w-6 ${intakeOn ? 'text-emerald-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">First-visit Medical History</h2>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                  When <span className="font-medium">on</span>, a patient with no history on file is asked for one before their first prescription, and the workspace does not open until it is saved. When <span className="font-medium">off</span>, nobody is asked and the workspace opens straight away.
                </p>
                {!intakeOn && (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 leading-relaxed">
                    Suspended. Histories already taken are untouched and still read on the Medical History tab, and the questions stay editable under Settings then Intake Questions. Turning this back on asks the same patients again.
                  </p>
                )}
              </div>
            </div>

            <div className="flex-shrink-0">
              <button
                onClick={handleToggleIntakeForm}
                disabled={!canEdit || savingIntake}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  intakeOn ? 'bg-emerald-600' : 'bg-gray-300'
                } ${(!canEdit || savingIntake) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                aria-label={intakeOn ? 'Suspend the first-visit medical history' : 'Ask for the first-visit medical history again'}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    intakeOn ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {!canEdit && (
            <p className="mt-4 text-xs text-gray-500 italic">You don't have permission to change this setting.</p>
          )}
        </div>

        {/* Save Template from Prescription toggle */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-start gap-4 flex-1">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${saveTemplateOn ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                <ClipboardList className={`h-6 w-6 ${saveTemplateOn ? 'text-emerald-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Save Template from Prescription</h2>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                  When <span className="font-medium">on</span>, a "Save Template" button appears in the prescription workspace so doctors can save the current prescription as a reusable template. When <span className="font-medium">off</span>, templates can only be created from the Templates screen.
                </p>
              </div>
            </div>

            <div className="flex-shrink-0">
              <button
                onClick={handleToggleSaveTemplate}
                disabled={!canEdit || saving}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  saveTemplateOn ? 'bg-emerald-600' : 'bg-gray-300'
                } ${(!canEdit || saving) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                aria-label={saveTemplateOn ? 'Disable Save Template in prescriptions' : 'Enable Save Template in prescriptions'}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    saveTemplateOn ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {!canEdit && (
            <p className="mt-4 text-xs text-gray-500 italic">You don't have permission to change this setting.</p>
          )}
        </div>

        {/* Medicine Pricing — global default profit markup applied to selling prices */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Percent className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Medicine Pricing</h2>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                Default profit markup applied to medicine selling prices. When recording a stock receipt,
                the sell price auto-fills to <span className="font-medium">purchase cost + this %</span>
                {' '}(you can still override it per item). e.g. at 30%, a Rs 50 cost suggests a Rs 65 sell price.
              </p>

              <div className="mt-4 flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Default markup</label>
                  <div className="relative w-40">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={markup}
                      onChange={(e) => setMarkup(e.target.value)}
                      disabled={!canEdit || savingMarkup}
                      className="w-full p-2.5 pr-9 border-2 border-gray-200 rounded-lg disabled:opacity-50"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">%</span>
                  </div>
                </div>
                {canEdit && (
                  <button
                    onClick={handleSaveMarkup}
                    disabled={savingMarkup}
                    className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {savingMarkup ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>

              {!canEdit && (
                <p className="mt-3 text-xs text-gray-500 italic">You don't have permission to change this setting.</p>
              )}
            </div>
          </div>
        </div>

        {/* Which supplies a bedside blood sugar check bills for. */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Droplet className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="flex-1 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Blood sugar check</h2>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                  When a doctor ticks "Check blood sugar here" on a prescription, these two go onto
                  the bill and come off the shelf when it is paid. They have to be named here: the
                  catalogue keeps the sugar strip next to the pregnancy strip, and the lancet next
                  to surgical blades, so nothing can pick them reliably on its own. Leave either
                  blank and it simply is not billed.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lancet</label>
                  <Select
                    value={sugar.lancetId}
                    onChange={(v) => setSugar((p) => ({ ...p, lancetId: v }))}
                    placeholder="Not set"
                    options={[{ value: '', label: 'Not set' }, ...supplies.map((m) => ({
                      value: m.id,
                      label: `${m.name}${m.quantity != null ? ` (${m.quantity} in stock)` : ''}`,
                    }))]}
                    disabled={!can('EDIT')}
                    buttonClassName="px-3 py-2 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Test strip</label>
                  <Select
                    value={sugar.stripId}
                    onChange={(v) => setSugar((p) => ({ ...p, stripId: v }))}
                    placeholder="Not set"
                    options={[{ value: '', label: 'Not set' }, ...supplies.map((m) => ({
                      value: m.id,
                      label: `${m.name}${m.quantity != null ? ` (${m.quantity} in stock)` : ''}`,
                    }))]}
                    disabled={!can('EDIT')}
                    buttonClassName="px-3 py-2 rounded-lg"
                  />
                </div>
              </div>

              {can('EDIT') && (
                <button
                  onClick={handleSaveSugar}
                  disabled={savingSugar}
                  className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-60"
                >
                  {savingSugar ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Billing counter: how a bill is rounded and how the receipt prints. */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Banknote className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="flex-1 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Billing</h2>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                  How the billing counter rounds a bill, and how the receipt prints. Paisa coins are no longer legal
                  tender, so a bill of Rs 1,117.76 cannot be paid exactly in cash. Cash is rounded down a second time,
                  because hunting for small change holds up the counter; a card or a transfer settles the exact figure.
                  Rounding shows as its own Round off line on the bill and the receipt.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="billing-rounding" className="block text-xs font-semibold text-gray-600 mb-1">Round each bill</label>
                  <Select
                    id="billing-rounding"
                    value={billing.roundingRule}
                    onChange={(v) => setBilling((b) => ({ ...b, roundingRule: v }))}
                    options={ROUNDING_RULE_OPTIONS}
                    disabled={!canEdit || savingBilling}
                  />
                </div>
                <div>
                  <label htmlFor="billing-rounding-cash" className="block text-xs font-semibold text-gray-600 mb-1">Round a cash bill further</label>
                  <Select
                    id="billing-rounding-cash"
                    value={billing.cashRoundingRule}
                    onChange={(v) => setBilling((b) => ({ ...b, cashRoundingRule: v }))}
                    options={ROUNDING_RULE_OPTIONS}
                    disabled={!canEdit || savingBilling}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Applied only when the patient pays in cash, on top of the rule above. Never rounds a cash bill
                    above what the same bill costs by card.
                  </p>
                </div>
                <div>
                  <label htmlFor="billing-paper" className="block text-xs font-semibold text-gray-600 mb-1">Receipt paper</label>
                  <Select
                    id="billing-paper"
                    value={billing.receiptPaper}
                    onChange={(v) => setBilling((b) => ({ ...b, receiptPaper: v }))}
                    options={PAPER_OPTIONS}
                    disabled={!canEdit || savingBilling}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p id="billing-autoprint-label" className="text-sm font-medium text-gray-900">Show the receipt to print after payment</p>
                  <p className="text-xs text-gray-500">Like a token slip: a preview, then Print or Skip. When off, the cashier opens it with Print receipt.</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={billing.receiptAutoPrint}
                  aria-labelledby="billing-autoprint-label"
                  onClick={() => setBilling((b) => ({ ...b, receiptAutoPrint: !b.receiptAutoPrint }))}
                  disabled={!canEdit || savingBilling}
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 ${
                    billing.receiptAutoPrint ? 'bg-emerald-600' : 'bg-gray-300'
                  } ${(!canEdit || savingBilling) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    billing.receiptAutoPrint ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {canEdit ? (
                <button
                  type="button"
                  onClick={handleSaveBilling}
                  disabled={savingBilling}
                  className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  {savingBilling ? 'Saving…' : 'Save billing settings'}
                </button>
              ) : (
                <p className="text-xs text-gray-500 italic">You don't have permission to change this setting.</p>
              )}
            </div>
          </div>
        </div>

        {/* Deleted-patient retention — when a soft-deleted patient is past this many days they're
            flagged in Deleted Patients for admin review/permanent deletion. */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Archive className="h-6 w-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Deleted-patient retention</h2>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                How long a deleted (archived) patient is kept before being flagged for review. Once a soft-deleted
                patient passes this many days, it's highlighted in <span className="font-medium">Deleted Patients</span>
                {' '}so an admin can restore it or permanently delete it. Records are always kept until then.
              </p>

              <div className="mt-4 flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Retention period</label>
                  <div className="relative w-40">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={retention}
                      onChange={(e) => setRetention(e.target.value)}
                      disabled={!canEdit || savingRetention}
                      className="w-full p-2.5 pr-12 border-2 border-gray-200 rounded-lg disabled:opacity-50"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">days</span>
                  </div>
                </div>
                {canEdit && (
                  <button
                    onClick={handleSaveRetention}
                    disabled={savingRetention}
                    className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {savingRetention ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>

              {!canEdit && (
                <p className="mt-3 text-xs text-gray-500 italic">You don't have permission to change this setting.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicalSettingsManagement;
